export interface Palette {
  skyTop: number;
  skyMid: number;
  skyBottom: number;
  ray: number;
  star: number;
  lane: number;
  /** Live circle colors — notes and grid cells resolve these each frame. */
  notePrimary: number;
  noteAccent: number;
}

export const GRAY_COLOR = 0x5b6068;

/** Calm (blue) ↔ mid (violet dusk) ↔ intense (red/orange), from the mockups. */
export const PALETTES: readonly Palette[] = [
  {
    skyTop: 0x1b1b52,
    skyMid: 0x4a6fd4,
    skyBottom: 0xa8e2f0,
    ray: 0xbfe6ff,
    star: 0xffffff,
    lane: 0xffffff,
    notePrimary: 0x29b6d8,
    noteAccent: 0x1d6fd1,
  },
  {
    skyTop: 0x241238,
    skyMid: 0x8a4a9e,
    skyBottom: 0xf0b8c8,
    ray: 0xf5d3ff,
    star: 0xffe9f5,
    lane: 0xffffff,
    notePrimary: 0x9a5bd8,
    noteAccent: 0xd84fa8,
  },
  {
    skyTop: 0x1a0b16,
    skyMid: 0xb32c1c,
    skyBottom: 0xffb347,
    ray: 0xffd9a0,
    star: 0xfff3d6,
    lane: 0xffffff,
    notePrimary: 0xd93b30,
    noteAccent: 0xff8c1a,
  },
];

export function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

export function lerpPalette(a: Palette, b: Palette, t: number): Palette {
  return {
    skyTop: lerpColor(a.skyTop, b.skyTop, t),
    skyMid: lerpColor(a.skyMid, b.skyMid, t),
    skyBottom: lerpColor(a.skyBottom, b.skyBottom, t),
    ray: lerpColor(a.ray, b.ray, t),
    star: lerpColor(a.star, b.star, t),
    lane: lerpColor(a.lane, b.lane, t),
    notePrimary: lerpColor(a.notePrimary, b.notePrimary, t),
    noteAccent: lerpColor(a.noteAccent, b.noteAccent, t),
  };
}

/** Continuous palette for an intensity 0..1 across the three tier palettes. */
export function paletteForIntensity(intensity: number): Palette {
  const v = Math.min(1, Math.max(0, intensity)) * 2;
  if (v <= 1) return lerpPalette(PALETTES[0]!, PALETTES[1]!, v);
  return lerpPalette(PALETTES[1]!, PALETTES[2]!, v - 1);
}

export function toCss(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}
